import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { Oval } from "react-loader-spinner";
import axiosInstance from "../../../hooks/axiosInstance";

const TYPE_LABELS = {
    standard: "Fixed",
    custom: "Custom",
    custom_service: "Custom Service",
};

const DELIVERY_BADGES = {
    sent: "bg-success",
    queued: "bg-warning",
    failed: "bg-danger",
};

const formatDate = (value) => {
    if (!value) return "—";
    return new Date(value).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const formatDateTime = (value) => {
    if (!value) return "—";
    return new Date(value).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const formatAmount = (value) =>
    Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const senderName = (item) =>
    item.sender ? `${item.sender.first_name ?? ""} ${item.sender.last_name ?? ""}`.trim() : "System";

export default function InvoiceHistory() {
    const [items, setItems] = useState([]);
    const [pagination, setPagination] = useState({});
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [invoiceType, setInvoiceType] = useState("");
    const [sendType, setSendType] = useState("");
    const [deliveryStatus, setDeliveryStatus] = useState("");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        const fetchHistories = async () => {
            setIsLoading(true);
            try {
                const response = await axiosInstance.get("user/invoice_history/fetch", {
                    params: {
                        page,
                        filter: searchQuery,
                        invoice_type: invoiceType,
                        send_type: sendType,
                        delivery_status: deliveryStatus,
                        start_date: startDate,
                        end_date: endDate,
                    },
                });
                setItems(response.data.data.histories.data);
                setPagination(response.data.data.histories);
            } catch (error) {
                console.error("Error fetching invoice histories:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchHistories();
    }, [page, searchQuery, invoiceType, sendType, deliveryStatus, startDate, endDate]);

    // Every filter change starts again from page 1
    const onFilter = (setter) => (event) => {
        setter(event.target.value);
        setPage(1);
    };

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= pagination.last_page) {
            setPage(newPage);
        }
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                        <h4 className="mb-sm-0" style={{ color: "#c4281c" }}>
                            Invoice Histories
                        </h4>
                        <div className="page-title-right">
                            <ol className="breadcrumb m-0">
                                <li className="breadcrumb-item">
                                    <Link to="/app/invoicing">Invoicing</Link>
                                </li>
                                <li className="breadcrumb-item active" style={{ color: "#c4281c" }}>
                                    Invoice Histories
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-body border border-dashed border-end-0 border-start-0">
                            <div className="row mb-3">
                                <div className="col-xl-4 col-md-6">
                                    <div className="search-box mb-2">
                                        <input
                                            type="text"
                                            className="form-control search"
                                            placeholder="Search account, product, email..."
                                            value={searchQuery}
                                            onChange={onFilter(setSearchQuery)}
                                        />
                                        <i className="ri-search-line search-icon"></i>
                                    </div>
                                </div>
                                <div className="col-xl-4 col-md-6">
                                    <div className="input-group mb-2">
                                        <span className="input-group-text">Sent from</span>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={startDate}
                                            onChange={onFilter(setStartDate)}
                                        />
                                    </div>
                                </div>
                                <div className="col-xl-4 col-md-6">
                                    <div className="input-group mb-2">
                                        <span className="input-group-text">Sent to</span>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={endDate}
                                            onChange={onFilter(setEndDate)}
                                        />
                                    </div>
                                </div>
                                <div className="col-xl-4 col-md-6">
                                    <div className="input-group mb-2">
                                        <span className="input-group-text">Invoice Type</span>
                                        <select className="form-select" value={invoiceType} onChange={onFilter(setInvoiceType)}>
                                            <option value="">All</option>
                                            <option value="standard">Fixed</option>
                                            <option value="custom">Custom</option>
                                            <option value="custom_service">Custom Service</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="col-xl-4 col-md-6">
                                    <div className="input-group mb-2">
                                        <span className="input-group-text">Sent By</span>
                                        <select className="form-select" value={sendType} onChange={onFilter(setSendType)}>
                                            <option value="">All</option>
                                            <option value="manual">Manual</option>
                                            <option value="automatic">Automatic</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="col-xl-4 col-md-6">
                                    <div className="input-group mb-2">
                                        <span className="input-group-text">Delivery</span>
                                        <select
                                            className="form-select"
                                            value={deliveryStatus}
                                            onChange={onFilter(setDeliveryStatus)}
                                        >
                                            <option value="">All</option>
                                            <option value="sent">Sent</option>
                                            <option value="queued">Queued</option>
                                            <option value="failed">Failed</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card-body">
                            {isLoading ? (
                                <div className="d-flex justify-content-center align-items-center" style={{ height: "40vh" }}>
                                    <Oval
                                        height={60}
                                        width={60}
                                        color="#FF0200"
                                        visible={true}
                                        ariaLabel="oval-loading"
                                        secondaryColor="#4fa94d"
                                        strokeWidth={2}
                                        strokeWidthSecondary={2}
                                    />
                                </div>
                            ) : (
                                <div className="table-responsive-sm">
                                    <table className="table align-middle table-nowrap table-striped table-borderless table-hover">
                                        <thead className="table-light text-muted">
                                            <tr>
                                                <th>#</th>
                                                <th>Date Sent</th>
                                                <th>Account</th>
                                                <th>Product</th>
                                                <th>Type</th>
                                                <th>Total</th>
                                                <th>Paid</th>
                                                <th>Balance</th>
                                                <th>Period</th>
                                                <th>Sent</th>
                                                <th>Delivery</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {items.length === 0 && (
                                                <tr>
                                                    <td colSpan="12" className="text-center text-muted py-4">
                                                        No invoice history found.
                                                    </td>
                                                </tr>
                                            )}
                                            {items.map((item, index) => (
                                                <tr key={item.id}>
                                                    <td>{(pagination.from || 1) + index}</td>
                                                    <td>{formatDateTime(item.sent_at || item.created_at)}</td>
                                                    <td>{item.company_name || "—"}</td>
                                                    <td>{item.product_name || "—"}</td>
                                                    <td>{TYPE_LABELS[item.invoice_type] || item.invoice_type}</td>
                                                    <td>₦{formatAmount(item.total_amount)}</td>
                                                    <td>₦{formatAmount(item.amount_paid)}</td>
                                                    <td>₦{formatAmount(item.balance)}</td>
                                                    <td>
                                                        {item.start_date
                                                            ? `${formatDate(item.start_date)} – ${formatDate(item.end_date)}`
                                                            : "—"}
                                                    </td>
                                                    <td>
                                                        <span
                                                            className={`badge ${item.send_type === "manual" ? "bg-info" : "bg-secondary"}`}
                                                            title={item.trigger || ""}
                                                        >
                                                            {item.send_type === "manual" ? "Manual" : "Automatic"}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${DELIVERY_BADGES[item.delivery_status] || "bg-light"}`}>
                                                            {item.delivery_status}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <button
                                                            className="btn btn-sm btn-soft-secondary"
                                                            onClick={() => setSelected(item)}
                                                        >
                                                            <i className="ri-eye-fill align-bottom"></i> View
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    <div className="d-flex justify-content-end mt-4 mb-4">
                                        <ul className="pagination listjs-pagination mb-0">
                                            {(pagination.links || []).map((link, index) => {
                                                const pageNumber = link.url
                                                    ? new URL(link.url).searchParams.get("page")
                                                    : null;
                                                return (
                                                    <li
                                                        key={index}
                                                        className={`page-item ${link.active ? "active" : ""} ${!link.url ? "disabled" : ""}`}
                                                    >
                                                        <button
                                                            className="page-link"
                                                            onClick={() => pageNumber && handlePageChange(Number(pageNumber))}
                                                            dangerouslySetInnerHTML={{ __html: link.label }}
                                                        />
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <Modal show={!!selected} onHide={() => setSelected(null)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Invoice History Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selected && (
                        <table className="table table-sm table-borderless mb-0">
                            <tbody>
                                <tr><th width="35%">Invoice</th><td>{TYPE_LABELS[selected.invoice_type]} #{selected.invoice_code || selected.invoice_id}</td></tr>
                                <tr><th>Account</th><td>{selected.company_name || "—"}</td></tr>
                                <tr><th>Customer</th><td>{selected.customer_name || "—"}</td></tr>
                                <tr><th>Product</th><td>{selected.product_name || "—"}</td></tr>
                                <tr><th>Category</th><td>{selected.category_name || "—"}{selected.sub_category_name ? ` / ${selected.sub_category_name}` : ""}</td></tr>
                                <tr><th>Quantity</th><td>{selected.quantity ?? 1}</td></tr>
                                <tr><th>Amount</th><td>₦{formatAmount(selected.amount)}</td></tr>
                                <tr><th>Discount</th><td>{Number(selected.discount || 0)}%</td></tr>
                                <tr><th>Amount after discount</th><td>₦{formatAmount(selected.amount_after_discount)}</td></tr>
                                <tr><th>VAT</th><td>{Number(selected.vat || 0)}% (₦{formatAmount(selected.vat_amount)})</td></tr>
                                <tr><th>Total</th><td>₦{formatAmount(selected.total_amount)}</td></tr>
                                <tr><th>Amount paid</th><td>₦{formatAmount(selected.amount_paid)}</td></tr>
                                <tr><th>Balance</th><td>₦{formatAmount(selected.balance)}</td></tr>
                                <tr><th>Payment status</th><td>{selected.payment_status || "—"}</td></tr>
                                <tr><th>Document type</th><td>{selected.document_type || "—"}</td></tr>
                                <tr><th>Cycle</th><td>{selected.circle ?? "—"}</td></tr>
                                <tr><th>Period</th><td>{formatDate(selected.start_date)} – {formatDate(selected.end_date)}</td></tr>
                                <tr><th>Notes</th><td>{selected.notes || "—"}</td></tr>
                                <tr><td colSpan="2"><hr className="my-1" /></td></tr>
                                <tr><th>Sent</th><td>{selected.send_type === "manual" ? `Manually by ${senderName(selected)}` : "Automatically"}</td></tr>
                                <tr><th>Reason</th><td>{selected.trigger || "—"}</td></tr>
                                <tr><th>Recipients</th><td>{(selected.recipients || []).join(", ") || "—"}</td></tr>
                                <tr><th>Delivery</th><td>{selected.delivery_status}{selected.error ? ` — ${selected.error}` : ""}</td></tr>
                                <tr><th>Date sent</th><td>{formatDateTime(selected.sent_at || selected.created_at)}</td></tr>
                            </tbody>
                        </table>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
}
